import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ShoppingCart, ArrowLeft, Edit2, Check, X } from "lucide-react";

interface Drill {
  id: string;
  title: string;
  description: string | null;
  price: number;
  preview_image_url: string | null;
  creator_id: string;
}

const Marketplace = () => {
  const [drills, setDrills] = useState<Drill[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [editingDrillId, setEditingDrillId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    loadDrills();
    getCurrentUser();
  }, []);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }
  };

  const loadDrills = async () => {
    try {
      const { data, error } = await supabase
        .from("drills")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDrills(data || []);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load drills. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (drill: Drill) => {
    setEditingDrillId(drill.id);
    setEditTitle(drill.title);
  };

  const cancelEditing = () => {
    setEditingDrillId(null);
    setEditTitle("");
  };

  const saveTitle = async (drillId: string) => {
    try {
      const { error } = await supabase
        .from("drills")
        .update({ title: editTitle })
        .eq("id", drillId);

      if (error) throw error;

      setDrills(drills.map(drill => 
        drill.id === drillId ? { ...drill, title: editTitle } : drill
      ));

      toast({
        title: "Success",
        description: "Drill title updated successfully!",
      });
      
      setEditingDrillId(null);
      setEditTitle("");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update drill title.",
      });
    }
  };

  const handlePurchase = async (drillId: string, price: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to purchase drills.",
        });
        return;
      }

      const { error } = await supabase
        .from("purchases")
        .insert({
          drill_id: drillId,
          buyer_id: user.id,
          price_paid: price,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Drill purchased successfully!",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to purchase drill. Please try again.",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <Link 
          to="/" 
          className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back Home
        </Link>
      </div>
      <h1 className="text-3xl font-bold mb-6">Drill Marketplace</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {drills.map((drill) => (
          <Card key={drill.id} className="flex flex-col">
            <CardHeader>
              <div className="flex justify-between items-center">
                {editingDrillId === drill.id ? (
                  <div className="flex gap-2 items-center flex-1">
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => saveTitle(drill.id)}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50 p-2"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditing}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <CardTitle className="text-xl">{drill.title}</CardTitle>
                    {currentUserId === drill.creator_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEditing(drill)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {drill.preview_image_url && (
                <img
                  src={drill.preview_image_url}
                  alt={drill.title}
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
              )}
              <p className="text-gray-600">{drill.description}</p>
              <p className="text-lg font-bold mt-2">${drill.price}</p>
            </CardContent>
            <CardFooter className="mt-auto">
              <Button
                className="w-full"
                onClick={() => handlePurchase(drill.id, drill.price)}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Purchase
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Marketplace;